import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { fetchFunnels } from '@/card/dealApi'
import { StateView } from '@/card/record/StateView'
import { Icon, type IconName } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatNumber } from '@/lib/format'
import { Pager, useClientPages } from '@/lists/Pager'
import { fetchFunnelConversion, fetchFunnelCycle, fetchLossReasons, fetchPnl, fetchUnitEconomics } from './api'
import {
  daysText,
  durationDays,
  Money,
  moneyText,
  num,
  NumberValue,
  percentText,
  PeriodBar,
  periodLabel,
  periodOf,
  QueryState,
  ShortId,
  shiftPeriod,
  singleCurrencyTotal,
  SkeletonLines,
  type StepPeriod,
  useNames,
} from './reportKit'
import { PNL_DIMENSIONS } from './types'
import type { PnlDimension, PnlLine, StageConversion, UnitEconomicsChannel } from './types'

/**
 * Отчёты по продажам (SalesReports.dc.html; ТЗ §4.6, тикет #76).
 *
 * Один экран, общий период: итоги, P&L в выбранном разрезе, конверсия воронки, цикл сделки,
 * CAC и LTV/CAC по каналам, причины проигрыша. Каждый блок — свой запрос и своё состояние:
 * «CAC не посчитан» не повод прятать P&L, и отказ по одному источнику называется в его
 * блоке, а не пустой таблицей.
 *
 * ## Разрез P&L — названиями, а не идентификаторами
 *
 * Сервер отдаёт разрез идентификатором (`dimensionId`). Название берётся из списка той же
 * сущности (`useNames`) — товар, клиент, сотрудник, отдел; список сужен областью видимости,
 * и чужая запись остаётся коротким идентификатором, а не раскрывается отчётом. У категорий
 * товара и территорий своего списка нет — там идентификатор, и это пробел, названный в
 * отчёте, а не замаскированный.
 *
 * ## Воронка — выбором из списка
 *
 * Воронок несколько (B2B, повторные продажи), и складывать их нельзя — это разные процессы.
 * Список берётся из `GET /sales/funnels`, по умолчанию — воронка по умолчанию.
 *
 * ## Чего нет из макета
 *
 * Гистограммы длительности сделок (сервер отдаёт только медиану и среднее), лимитов этапов
 * и плана маржи (в API их нет), канала как разреза P&L, экспорта и «Добавить в рассылку»
 * (у готового отчёта нет ни выгрузки, ни сохранённого определения для расписания).
 */

/** Сущность списка, из которой берутся названия разреза; `null` — списка нет. */
const DIMENSION_NAMES: Record<PnlDimension, { entity: string; columns: string[] } | null> = {
  PRODUCT: { entity: 'sales.product', columns: ['name'] },
  COUNTERPARTY: { entity: 'sales.counterparty', columns: ['name'] },
  EMPLOYEE: { entity: 'hr.employee', columns: ['last_name', 'first_name'] },
  DEPARTMENT: { entity: 'hr.department', columns: ['name'] },
  PRODUCT_CATEGORY: null,
  TERRITORY: null,
}
const DIMENSION_PATH: Partial<Record<PnlDimension, string>> = {
  PRODUCT: '/products',
  COUNTERPARTY: '/counterparties',
  EMPLOYEE: '/employees',
  DEPARTMENT: '/departments',
}

export function SalesReportsPage() {
  const { t, locale, user } = useApp()
  const [period, setPeriod] = useState<StepPeriod>(() => periodOf('quarter', new Date()))
  const [dimension, setDimension] = useState<PnlDimension>('EMPLOYEE')
  const [chosenFunnel, setChosenFunnel] = useState<string | null>(null)

  const mayDeals = can(user, 'sales.deal', 'read')
  const funnels = useQuery({
    queryKey: ['funnels'],
    enabled: mayDeals,
    queryFn: ({ signal }) => fetchFunnels(signal),
    retry: false,
  })
  const funnelId = chosenFunnel ?? funnels.data?.find((f) => f.defaultFunnel)?.id ?? funnels.data?.[0]?.id ?? null

  const range = { from: period.from, to: period.to }
  const pnl = useQuery({
    queryKey: ['pnl', dimension, range],
    queryFn: ({ signal }) => fetchPnl(dimension, range, signal),
    retry: false,
  })
  const conversion = useQuery({
    queryKey: ['funnel-conversion', funnelId, range],
    enabled: Boolean(funnelId),
    queryFn: ({ signal }) => fetchFunnelConversion(funnelId!, range, signal),
    retry: false,
  })
  const cycle = useQuery({
    queryKey: ['funnel-cycle', funnelId, range],
    enabled: Boolean(funnelId),
    queryFn: ({ signal }) => fetchFunnelCycle(funnelId!, range, signal),
    retry: false,
  })
  const losses = useQuery({
    queryKey: ['funnel-losses', funnelId, range],
    enabled: Boolean(funnelId),
    queryFn: ({ signal }) => fetchLossReasons(funnelId!, range, signal),
    retry: false,
  })
  const unit = useQuery({
    queryKey: ['unit-economics', range],
    queryFn: ({ signal }) => fetchUnitEconomics(range, signal),
    retry: false,
  })

  const label = periodLabel(period, t, locale)
  const allEmpty =
    pnl.isSuccess && pnl.data.lines.length === 0 &&
    (!funnelId || (conversion.isSuccess && conversion.data.every((s) => s.movedOn + s.won + s.lost === 0))) &&
    unit.isSuccess && unit.data.length === 0

  return (
    <div className="stack" style={{ gap: 14 }}>
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <span>{t('nav.section.analytics')}</span>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{t('nav.salesReports')}</span>
      </nav>
      <div className="page-head">
        <h1 className="h1">{t('nav.salesReports')}</h1>
      </div>

      <div className="toolbar">
        <PeriodBar value={period} onChange={setPeriod} />
        <div className="vsep" />
        <span className="t-sm muted">{t('sr.cut')}</span>
        <div className="seg" role="group" aria-label={t('report.pnl.dimension')}>
          {PNL_DIMENSIONS.map((option) => (
            <button key={option} type="button" aria-pressed={dimension === option} onClick={() => setDimension(option)}>
              {t(`sr.dim.${option}`)}
            </button>
          ))}
        </div>
        <span className="spacer" />
        {mayDeals && (funnels.data?.length ?? 0) > 0 && (
          <label className="row t-sm" style={{ gap: 6 }}>
            <span className="muted">{t('report.funnel.id')}</span>
            <select
              className="input input-sm select"
              value={funnelId ?? ''}
              onChange={(event) => setChosenFunnel(event.target.value)}
            >
              {(funnels.data ?? []).map((funnel) => (
                <option key={funnel.id} value={funnel.id}>
                  {funnel.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {allEmpty ? (
        <StateView
          kind="empty"
          boxed
          title={t('sr.empty.title', { period: label })}
          description={t('sr.empty.text')}
          actions={
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => setPeriod(shiftPeriod(period, -1))}>
              <Icon name="arrow-left" size="sm" />
              {t('sr.empty.prev', { period: periodLabel(shiftPeriod(period, -1), t, locale) })}
            </button>
          }
        />
      ) : (
        <>
          <SummaryFacts
            pnl={pnl.data}
            conversion={conversion.data}
            cycle={cycle.data?.[0]}
            losses={losses.data}
          />

          <div className="wa-grid">
            <PnlPanel
              className="wa-wide"
              dimension={dimension}
              periodText={label}
              isLoading={pnl.isLoading}
              error={pnl.error}
              onRetry={() => void pnl.refetch()}
              lines={pnl.data?.lines}
              complete={pnl.data?.complete ?? true}
              linesWithoutCost={pnl.data?.linesWithoutCost ?? 0}
            />

            <Panel id="sr-fn" title={t('sr.funnel.title')} count={funnels.data?.find((f) => f.id === funnelId)?.name}>
              {!mayDeals ? (
                <QueryState error={forbiddenMarker} errorTitle="" compact />
              ) : funnels.isSuccess && funnels.data.length === 0 ? (
                <StateView kind="setup" compact title={t('sr.funnel.none')} description={t('sr.funnel.noneText')} />
              ) : conversion.isLoading || funnels.isLoading ? (
                <SkeletonLines />
              ) : conversion.error ? (
                <QueryState compact error={conversion.error} errorTitle={t('sr.funnel.error')} onRetry={() => void conversion.refetch()} />
              ) : (
                <ConversionTable stages={conversion.data ?? []} />
              )}
            </Panel>

            <Panel id="sr-cy" title={t('report.funnel.cycle')}>
              {!mayDeals ? (
                <QueryState error={forbiddenMarker} errorTitle="" compact />
              ) : cycle.isLoading ? (
                <SkeletonLines rows={3} />
              ) : cycle.error ? (
                <QueryState compact error={cycle.error} errorTitle={t('sr.cycle.error')} onRetry={() => void cycle.refetch()} />
              ) : (
                <CycleBody cycle={cycle.data?.[0]} stages={conversion.data ?? []} />
              )}
            </Panel>

            <Panel id="sr-cac" title={t('sr.cac.title')}>
              {unit.isLoading ? (
                <SkeletonLines />
              ) : unit.error ? (
                <QueryState compact error={unit.error} errorTitle={t('sr.cac.error')} onRetry={() => void unit.refetch()} />
              ) : (unit.data ?? []).length === 0 ? (
                <StateView kind="empty" compact title={t('sr.cac.empty')} />
              ) : (
                <CacTable channels={unit.data ?? []} />
              )}
            </Panel>

            <LossesPanel
              isLoading={losses.isLoading}
              error={mayDeals ? losses.error : forbiddenMarker}
              onRetry={() => void losses.refetch()}
              reasons={losses.data ?? []}
            />
          </div>
        </>
      )}
    </div>
  )
}

/** Отказ, известный до запроса: права на источник нет, и запрос не уходит вовсе. */
const forbiddenMarker = new ApiError(403, null, '')

function Panel({
  id,
  title,
  count,
  extra,
  className,
  children,
  footer,
}: {
  id: string
  title: string
  count?: ReactNode
  extra?: ReactNode
  className?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <section className={className ? `panel ${className}` : 'panel'} aria-labelledby={id} style={{ overflow: 'hidden' }}>
      <div className="panel-h">
        <span className="h2" id={id}>{title}</span>
        {count && <span className="count">{count}</span>}
        <span className="spacer" />
        {extra}
      </div>
      {children}
      {footer && <div className="panel-f muted">{footer}</div>}
    </section>
  )
}

function SummaryFacts({
  pnl,
  conversion,
  cycle,
  losses,
}: {
  pnl: { totalRevenue: string; totalMargin: string; lines: PnlLine[] } | undefined
  conversion: StageConversion[] | undefined
  cycle: { dealCount: number; averageCycle: string | null; medianCycle: string | null } | undefined
  losses: { dealCount: number; lostAmount: string | null; currency: string | null; reason: string | null }[] | undefined
}) {
  const { t, locale } = useApp()
  const currency = pnl ? singleCurrencyTotal(pnl.lines, (l) => l.revenue, (l) => l.currency)?.currency ?? null : null
  const revenue = num(pnl?.totalRevenue)
  const margin = num(pnl?.totalMargin)
  const marginPct = revenue && margin != null ? (margin / revenue) * 100 : null
  const won = conversion ? conversion.reduce((sum, stage) => sum + stage.won, 0) : null
  const lost = losses ? losses.reduce((sum, reason) => sum + reason.dealCount, 0) : null
  const lostMoney = losses ? singleCurrencyTotal(losses, (r) => r.lostAmount, (r) => r.currency) : null
  const topReason = losses?.slice().sort((a, b) => b.dealCount - a.dealCount)[0]

  const fact = (key: string, value: string, sub?: ReactNode) => (
    <div className="fact">
      <span className="k">{t(key)}</span>
      <span className="v">{value}</span>
      {sub && <span className="s">{sub}</span>}
    </div>
  )
  return (
    <div className="facts">
      {fact('sr.fact.revenue', moneyText(revenue, currency, locale))}
      {fact('sr.fact.margin', moneyText(margin, currency, locale), marginPct != null ? t('sr.fact.marginPct', { pct: percentText(marginPct, locale) }) : undefined)}
      {fact('sr.fact.won', won == null ? '—' : formatNumber(won, locale))}
      {fact(
        'sr.fact.cycle',
        daysText(durationDays(cycle?.medianCycle), t, locale),
        cycle ? t('sr.fact.cycleSub', { avg: daysText(durationDays(cycle.averageCycle), t, locale) }) : undefined,
      )}
      {fact(
        'sr.fact.lost',
        lost == null ? '—' : formatNumber(lost, locale),
        lostMoney && lost
          ? t('sr.fact.lostSub', {
              sum: moneyText(lostMoney.amount, lostMoney.currency, locale),
              reason: topReason?.reason ?? t('report.funnel.noReason'),
            })
          : undefined,
      )}
    </div>
  )
}

function PnlPanel({
  className,
  dimension,
  periodText,
  isLoading,
  error,
  onRetry,
  lines,
  complete,
  linesWithoutCost,
}: {
  className: string
  dimension: PnlDimension
  periodText: string
  isLoading: boolean
  error: unknown
  onRetry: () => void
  lines: PnlLine[] | undefined
  complete: boolean
  linesWithoutCost: number
}) {
  const { t, locale } = useApp()
  const naming = DIMENSION_NAMES[dimension]
  const ids = (lines ?? []).map((line) => (line.dimensionId == null ? null : String(line.dimensionId)))
  const { names } = useNames(naming?.entity ?? null, naming?.columns ?? [], ids)
  const sorted = (lines ?? []).slice().sort((a, b) => (num(b.revenue) ?? 0) - (num(a.revenue) ?? 0))
  const { visible, pager } = useClientPages(sorted)
  const revenueTotal = singleCurrencyTotal(sorted, (l) => l.revenue, (l) => l.currency)
  const costTotal = singleCurrencyTotal(sorted, (l) => l.cost, (l) => l.currency)
  // Итог маржи — только если она известна у всех строк: частичная сумма польстила бы.
  const marginTotal = sorted.every((l) => l.margin != null) ? singleCurrencyTotal(sorted, (l) => l.margin, (l) => l.currency) : null
  const path = DIMENSION_PATH[dimension]

  return (
    <Panel
      id="sr-pl"
      className={className}
      title={t('sr.pnl.title', { cut: t(`sr.dim.${dimension}`) })}
      count={`${periodText} · ${sorted.length}`}
      extra={<span className="t-sm muted">{t('sr.pnl.noVat')}</span>}
      footer={
        naming == null ? (
          <span>{t('sr.pnl.noNames')}</span>
        ) : (
          <span>{t('sr.pnl.footer')}</span>
        )
      }
    >
      {isLoading ? (
        <SkeletonLines rows={6} />
      ) : error ? (
        <QueryState compact error={error} errorTitle={t('sr.pnl.error')} onRetry={onRetry} />
      ) : sorted.length === 0 ? (
        <StateView kind="empty" compact title={t('sr.pnl.empty')} />
      ) : (
        <>
          {!complete && (
            // Неполнота названа вслух: маржа посчитана не по всем строкам, и цифра меньше
            // настоящей. Молчать об этом значит выдать неполную маржу за полную.
            <div className="panel-b" style={{ paddingBottom: 0 }}>
              <div className="alert alert-warning" role="status">
                <Icon name="alert" />
                <span className="fg">{t('report.pnl.incomplete', { count: linesWithoutCost })}</span>
              </div>
            </div>
          )}
          <div className="wa-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t(`sr.dim.${dimension}`)}</th>
                  <th className="num">{t('report.pnl.revenue')}</th>
                  <th className="num">{t('report.pnl.cost')}</th>
                  <th className="num">{t('sr.pnl.gross')}</th>
                  <th className="num">{t('report.pnl.marginPercent')}</th>
                  <th style={{ width: 220 }}>{t('sr.pnl.share')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((line, index) => {
                  const id = line.dimensionId == null ? null : String(line.dimensionId)
                  const name = id ? names.get(id) : null
                  const share = revenueTotal && revenueTotal.amount > 0 ? ((num(line.revenue) ?? 0) / revenueTotal.amount) * 100 : null
                  return (
                    <tr key={id ?? `none-${index}`}>
                      <td>
                        {id == null ? (
                          <span className="secondary">{t('sr.pnl.unassigned')}</span>
                        ) : name ? (
                          path ? <Link to={`${path}/${id}`}>{name}</Link> : name
                        ) : (
                          <ShortId id={id} />
                        )}
                      </td>
                      <td className="num"><Money value={line.revenue} currency={line.currency} /></td>
                      <td className="num secondary"><Money value={line.cost} currency={line.currency} /></td>
                      {/* null — себестоимость известна не для всех строк группы: ноль польстил бы марже. */}
                      <td className="num strong"><Money value={line.margin} currency={line.currency} /></td>
                      <td className="num">{percentText(num(line.marginPercent), locale)}</td>
                      <td>
                        <div className="wa-share">
                          <div className="hbar-track" role="img" aria-label={t('sr.pnl.shareOf', { pct: percentText(share, locale) })}>
                            <div className="hbar" style={{ width: `${Math.max(0, Math.min(100, share ?? 0))}%` }} />
                          </div>
                          <span className="t-sm num">{percentText(share, locale)}</span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                <tr className="total">
                  <td>{t('sr.total')}</td>
                  <td className="num">{revenueTotal ? moneyText(revenueTotal.amount, revenueTotal.currency, locale) : '—'}</td>
                  <td className="num">{costTotal && complete ? moneyText(costTotal.amount, costTotal.currency, locale) : '—'}</td>
                  <td className="num">{marginTotal ? moneyText(marginTotal.amount, marginTotal.currency, locale) : '—'}</td>
                  <td className="num">
                    {marginTotal && revenueTotal?.amount ? percentText((marginTotal.amount / revenueTotal.amount) * 100, locale) : '—'}
                  </td>
                  <td>
                    <span className="t-sm">100 %</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Pager {...pager} />
        </>
      )}
    </Panel>
  )
}

function ConversionTable({ stages }: { stages: StageConversion[] }) {
  const { t, locale } = useApp()
  const ordered = stages.slice().sort((a, b) => a.stageOrder - b.stageOrder)
  if (ordered.length === 0) return <StateView kind="empty" compact title={t('sr.funnel.empty')} />
  return (
    <div className="wa-scroll">
      <table className="tbl" aria-label={t('sr.funnel.title')}>
        <thead>
          <tr>
            <th>{t('report.funnel.stage')}</th>
            <th className="num">{t('report.funnel.movedOn')}</th>
            {/* Возвраты отдельно, а не вычтены: стадия, на которую возвращаются, — находка отчёта. */}
            <th className="num">{t('report.funnel.movedBack')}</th>
            <th className="num">{t('report.funnel.won')}</th>
            <th className="num">{t('report.funnel.lost')}</th>
            <th className="num">{t('report.funnel.conversion')}</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((stage, index) => (
            <tr key={stage.stageId}>
              <td>
                <span className="stage">
                  <span className="pips" aria-hidden="true">
                    {ordered.map((_, at) => (
                      <i key={at} className={at <= index ? 'on' : undefined} />
                    ))}
                  </span>
                  {stage.stageName}
                </span>
              </td>
              <td className="num">{formatNumber(stage.movedOn, locale)}</td>
              <td className="num">{stage.movedBack > 0 ? <span className="strong">{formatNumber(stage.movedBack, locale)}</span> : <span className="muted">0</span>}</td>
              <td className="num">{formatNumber(stage.won, locale)}</td>
              <td className="num">{formatNumber(stage.lost, locale)}</td>
              {/* null, а не 0: «все застряли» и «никто не доходил» — разные факты. */}
              <td className="num strong">{percentText(num(stage.conversionPercent), locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CycleBody({
  cycle,
  stages,
}: {
  cycle: { dealCount: number; averageCycle: string | null; medianCycle: string | null } | undefined
  stages: StageConversion[]
}) {
  const { t, locale } = useApp()
  const perStage = stages
    .slice()
    .sort((a, b) => a.stageOrder - b.stageOrder)
    .map((stage) => ({ name: stage.stageName, days: durationDays(stage.averageTimeInStage) }))
  const max = Math.max(1, ...perStage.map((s) => s.days ?? 0))
  return (
    <div className="panel-b stack" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 28, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="stack" style={{ gap: 0 }}>
          <span className="t-sm muted">{t('report.funnel.medianCycle')}</span>
          <span className="kpi-num">{daysText(durationDays(cycle?.medianCycle), t, locale)}</span>
        </span>
        <span className="stack" style={{ gap: 0 }}>
          <span className="t-sm muted">{t('report.funnel.avgCycle')}</span>
          <span className="kpi-num">{daysText(durationDays(cycle?.averageCycle), t, locale)}</span>
        </span>
        <span className="t-sm secondary">{t('sr.cycle.deals', { count: cycle?.dealCount ?? 0 })}</span>
      </div>
      {perStage.length > 0 && (
        <div className="stack" style={{ gap: 8, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <span className="h3">{t('sr.cycle.perStage')}</span>
          {perStage.map((stage) => (
            <div key={stage.name} className="wa-stage-days">
              <span className="t-sm secondary ellipsis">{stage.name}</span>
              <div className="hbar-track" role="img" aria-label={`${stage.name}: ${daysText(stage.days, t, locale)}`}>
                <div className="hbar" style={{ width: `${((stage.days ?? 0) / max) * 100}%` }} />
              </div>
              <span className="t-sm strong num">{daysText(stage.days, t, locale)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Окупаемость канала словами: цвет не единственный носитель смысла. */
function payback(ratio: number | null): { cls: string; icon: IconName; key: string } | null {
  if (ratio == null) return null
  if (ratio >= 3) return { cls: 'badge b-success', icon: 'check', key: 'sr.cac.pays' }
  if (ratio >= 1) return { cls: 'badge b-warning', icon: 'alert', key: 'sr.cac.edge' }
  return { cls: 'badge b-danger', icon: 'close', key: 'sr.cac.loss' }
}

function CacTable({ channels }: { channels: UnitEconomicsChannel[] }) {
  const { t, locale } = useApp()
  return (
    <>
      <div className="wa-scroll">
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('report.unit.channel')}</th>
              <th className="num">{t('report.unit.spend')}</th>
              <th className="num">{t('sr.cac.new')}</th>
              <th className="num">{t('report.unit.cac')}</th>
              <th className="num">{t('report.unit.ltv')}</th>
              <th>{t('report.unit.ratio')}</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((channel, index) => {
              const ratio = num(channel.ltvToCac)
              const verdict = payback(ratio)
              return (
                <tr key={`${channel.channel}-${channel.medium}-${index}`}>
                  <td>
                    {channel.channel ?? t('sr.cac.noChannel')}
                    {channel.medium ? <span className="muted"> / {channel.medium}</span> : null}
                  </td>
                  {/* null, а не ноль: незаполненные затраты — не доказательство бесплатных клиентов. */}
                  <td className="num"><Money value={channel.acquisitionSpend} currency={channel.currency} /></td>
                  <td className="num">{formatNumber(channel.customerCount, locale)}</td>
                  <td className="num"><Money value={channel.customerAcquisitionCost} currency={channel.currency} /></td>
                  <td className="num"><Money value={channel.averageLifetimeValue} currency={channel.currency} /></td>
                  <td>
                    <span className="row" style={{ gap: 8 }}>
                      <span className="strong num"><NumberValue value={channel.ltvToCac} digits={1} /></span>
                      {verdict && (
                        <span className={verdict.cls}>
                          <Icon name={verdict.icon} />
                          {t(verdict.key)}
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="panel-f muted" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <span>{t('sr.cac.footer')}</span>
        <span>{t('sr.cac.thresholds')}</span>
      </div>
    </>
  )
}

function LossesPanel({
  isLoading,
  error,
  onRetry,
  reasons,
}: {
  isLoading: boolean
  error: unknown
  onRetry: () => void
  reasons: { reason: string | null; dealCount: number; lostAmount: string | null; currency: string | null }[]
}) {
  const { t, locale } = useApp()
  const [by, setBy] = useState<'count' | 'amount'>('count')
  const money = singleCurrencyTotal(reasons, (r) => r.lostAmount, (r) => r.currency)
  const count = reasons.reduce((sum, r) => sum + r.dealCount, 0)
  // Длина по сумме — только если валюта одна: рубли с евро на одной шкале не сравнить.
  const measure = (r: (typeof reasons)[number]) => (by === 'amount' && money ? num(r.lostAmount) ?? 0 : r.dealCount)
  const max = Math.max(1, ...reasons.map(measure))
  const sorted = reasons.slice().sort((a, b) => measure(b) - measure(a))

  return (
    <Panel
      id="sr-rs"
      title={t('report.funnel.losses')}
      count={reasons.length > 0 ? t('sr.losses.count', { count, sum: money ? moneyText(money.amount, money.currency, locale) : '—' }) : undefined}
      extra={
        money ? (
          <div className="seg" role="group" aria-label={t('sr.losses.by')}>
            <button type="button" aria-pressed={by === 'count'} onClick={() => setBy('count')}>{t('sr.losses.byCount')}</button>
            <button type="button" aria-pressed={by === 'amount'} onClick={() => setBy('amount')}>{t('sr.losses.byAmount')}</button>
          </div>
        ) : undefined
      }
      footer={<span>{t('sr.losses.footer')}</span>}
    >
      {isLoading ? (
        <SkeletonLines />
      ) : error ? (
        <QueryState compact error={error} errorTitle={t('sr.losses.error')} onRetry={onRetry} />
      ) : reasons.length === 0 ? (
        <StateView kind="empty" compact title={t('sr.losses.empty')} />
      ) : (
        <div className="panel-b stack" style={{ gap: 12 }}>
          {sorted.map((reason, index) => {
            const share = money && money.amount > 0 ? ((num(reason.lostAmount) ?? 0) / money.amount) * 100 : null
            return (
              <div key={`${reason.reason}-${index}`} className="stack wa-reason" style={{ gap: 4 }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                  <span className="ellipsis">{reason.reason ?? t('report.funnel.noReason')}</span>
                  <span className="num nowrap">
                    <span className="strong">{formatNumber(reason.dealCount, locale)}</span>{' '}
                    <span className="muted t-sm">
                      · {moneyText(reason.lostAmount, reason.currency, locale)}
                      {share != null ? ` · ${percentText(share, locale)}` : ''}
                    </span>
                  </span>
                </div>
                <div className="hbar-track" role="img" aria-label={`${reason.reason ?? t('report.funnel.noReason')}: ${reason.dealCount}`}>
                  <div className="hbar" style={{ width: `${(measure(reason) / max) * 100}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Panel>
  )
}
